/**
 * moirefallPipeline — moiré interference between two near-aligned
 * line fields that drift apart.
 *
 * Two superimposed line patterns: a vertical-line grid and a tilted
 * line grid. Where the lines intersect, bright beating patterns
 * appear due to moiré interference. The two grids drift apart in
 * tilt/phase over time, so the moiré pattern evolves continuously.
 *
 * Innovations:
 *   - The interference is computed by sampling the difference of the
 *     two pattern amplitudes: where both are bright, we get a beat.
 *   - The "fall" comes from the moiré pattern appearing to fall
 *     (move downward) as the underlying grids drift apart.
 *   - Per-line color shift: the lines from grid A and grid B have
 *     slightly different hues, so the moiré beats create rainbow
 *     fringes.
 *   - FBM-modulated line spacing, so the grid is locally irregular
 *     and the moiré pattern doesn't look like a perfect interference
 *     lattice.
 *
 * Audio:
 *   - bass: drift speed (how fast the grids separate)
 *   - mid:  line spacing (denser lines = more beat frequency)
 *   - high: contrast (sharper fringes via saturation)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MoirefallPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Sample a line pattern aligned with direction (angle) radians.
// Returns a value in [0, 1] that is high ON the lines, low in between.
float lineGrid(vec2 p, float angle, float spacing, float thickness, float offset) {
  float ca = cos(angle);
  float sa = sin(angle);
  // Project onto the perpendicular.
  float proj = -sa * p.x + ca * p.y;
  // Make spacing modulated by FBM.
  float modS = spacing + 0.20 * valueNoise(p * 0.5 + vec2(offset, 0.0));
  float scaled = (proj + offset) / modS;
  // Distance to nearest line.
  float d = abs(fract(scaled) - 0.5) * modS;
  return 1.0 - smoothstep(0.0, thickness, d);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Two grids with slightly different angles. Their relative angle
  // grows over time (the "drift apart").
  float angleA = 0.0;
  float angleB = 0.30 + 0.20 * sin(uTime * 0.3 + bass * 0.5);
  // Spacing.
  float spacing = 0.05 + 0.05 * mid;
  // Offsets — animate these so the patterns "fall" (move downward).
  float offsetA = uTime * 0.5 * (0.4 + bass * 0.5);
  float offsetB = uTime * 0.5 * (0.3 + bass * 0.4);
  // Line thickness.
  float thickness = 0.005 + 0.003 * mid;

  float gridA = lineGrid(p, angleA, spacing, thickness, offsetA);
  float gridB = lineGrid(p, angleB, spacing, thickness, offsetB);

  // Background — dark cool gradient.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.12),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  // Each grid has its own hue.
  vec3 colA = vec3(0.4, 0.7, 1.0);
  vec3 colB = vec3(1.0, 0.5, 0.7);

  // Layer 1: just the grids.
  vec3 col = bg;
  col += colA * gridA * 0.4;
  col += colB * gridB * 0.4;

  // The moiré beat: where both grids are bright, we get a strong
  // interference. Use product for sharpest peaks.
  float beat = gridA * gridB;
  // Visualize the beat as a warm color.
  vec3 beatColor = vec3(1.0, 0.85, 0.5);
  col += beatColor * beat * (1.5 + 1.5 * high);

  // "Falling" effect: the beats have a vertical fall component.
  // Make the beats brighter at the top, fading as they fall.
  float fall = exp(-max(p.y + 0.5, 0.0) * 1.5);
  col += beat * beatColor * fall * 0.5;

  // Add an "anti-moiré" pattern in the gaps — soft interference
  // ripples that appear between the bright lines.
  float ripple = abs(gridA - gridB);
  col += vec3(0.4, 0.7, 1.0) * ripple * 0.2;

  // Streak: where the beat is strong, draw a thin vertical highlight
  // that travels down with the offset.
  float streak = step(0.5, beat) * step(0.0, fract(p.y * 30.0 - offsetA * 5.0));
  col += vec3(1.0, 1.0, 0.8) * streak * 0.5;

  // A few "drift particles" — small bright dots that follow the
  // tilt-axis intersection lines.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    vec2 pp = vec2(
      sin(uTime * 0.2 + fi * 1.7) * 0.8,
      -0.7 + fract(uTime * 0.5 + fi * 0.31) * 1.4
    );
    float dd = length(p - pp);
    col += vec3(1.0, 0.9, 0.7) * exp(-dd * 200.0) * 0.7;
  }

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeMoirefallBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeMoirefallRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMoirefallPipeline(gl: WebGL2RenderingContext): MoirefallPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMoirefall(
  gl: WebGL2RenderingContext,
  pipe: MoirefallPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
